import { getGroup, orUndefined } from '@lms/api-client/backend';
import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import BasicDataForm from '@/features/groups/components/basic-group-data';
import { GroupStats } from '@/features/groups/components/group-stats';
import PersonList from '@/features/groups/components/personlist';

// Ports app/[brand]/admin/groups/[id]/page.tsx. The server-side group fetch
// becomes an SWR read; `notFound()` becomes an inline message and
// `revalidatePath` becomes SWR `mutate`. Codes are managed from /admin/codes,
// not here. Rendered inside the admin <Shell> by admin-app.tsx.
export function GroupDetail() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const isValidId = Number.isFinite(groupId);
  const { brand: site, slug } = useBrand();
  const { access } = useSiteAccess(slug);
  const canManage = !!access?.canManageGroups;
  const t = useTranslations('admin');
  const [editOpen, setEditOpen] = useState(false);

  const {
    data: group,
    isLoading: groupLoading,
    mutate: mutateGroup,
  } = useSWR(isValidId ? ['admin/group', groupId] : null, () =>
    orUndefined(getGroup(groupId)),
  );

  if (groupLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!group) {
    return (
      <PageBox>
        <PageHeader backHref={`/${slug}/admin/groups`}>
          <PageTitle>{t('go_back')}</PageTitle>
        </PageHeader>
      </PageBox>
    );
  }

  return (
    <PageBox>
      <PageHeader backHref={`/${slug}/admin/groups`}>
        <PageTitle>{group.name}</PageTitle>
        <PageButtons>
          {canManage && (
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {t('edit', {
                    what: t(`${site.groupAlias}_singular`).toLowerCase(),
                  })}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {t('edit', {
                      what: t(`${site.groupAlias}_singular`).toLowerCase(),
                    })}
                  </DialogTitle>
                  <DialogDescription>
                    <BasicDataForm
                      site={site}
                      group={group}
                      onSaved={() => {
                        setEditOpen(false);
                        mutateGroup();
                      }}
                    />
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          )}
        </PageButtons>
      </PageHeader>

      <GroupStats groupId={group.id} />

      <PersonList
        groupId={group.id}
        groupName={group.name}
        canManage={canManage}
      />
    </PageBox>
  );
}
